import { useEffect, useState } from 'react';
import {
  IApproachingData,
  IGetNewShopsParams,
  INewShop,
  ISaleStaff,
  ITeam,
  IGetDailyReportParams,
  IJobProcessStatisticTree,
  IJobProcessStatisticTeam,
  IShopTakeCare,
  IGetShopTakeCareParams,
  IGetShopTakeCareStaffParams,
  IShopTakeCareStaff,
} from 'interfaces';
import {
  IGetMerchantApproachingParams,
  IMerchantApproachingProcess,
  IUpdateMerchantApproaching,
  IMerchantApproachHistory,
} from 'interfaces/merchant';
import reportApi from '../repos/tekoApis/reportApi';
import {
  reportConstants,
  commonConstants,
  dateTimeConstants,
} from 'constants/index';
import { AxiosResponse } from 'axios';
import { PaginationProps } from 'antd/es/pagination';
import {
  fileService,
  merchantService,
  reportServices,
  shopServices,
} from 'services';
import { paginationHelpers, commonHelpers } from 'helpers';
import { pickBy, omit, orderBy } from 'lodash';
import { AnyObject } from 'interfaces';
import { defaultPage } from 'constants/pagination';
import dayjs from 'dayjs';

const { countFilter } = commonHelpers;
const { DEFAULT_SIZE_CHANGER, ALL_VALUE_NUMBER } = commonConstants;
const {
  DEFAULT_PARAMS_DAILY_REPORT,
  REPORTABLE_ACCORDING_TO,
} = reportConstants;
const { mapPagerApiToAntdPager } = paginationHelpers;
const { DATE_SLASH_FORMAT, YEAR_DASH_FORMAT } = dateTimeConstants;
const { DEFAULT_PARAMS } = commonConstants;

const useApproachingReport = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(reportConstants.DEFAULT_PARAMS);
  const [data, setData] = useState<IApproachingData>();
  const getData = async () => {
    try {
      setLoading(true);
      const result = await reportApi.getApproachingReport(filters);
      setData(result.data);
    } catch {
      setData(undefined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      getData().then();
    }
  }, [filters]);

  return {
    loading,
    filters,
    setFilters,
    data,
  };
};

const saveReport = (response: AxiosResponse) => {
  const contentType =
    response.headers['content-type'] ||
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const suggestedFileName = response.headers['content-disposition']?.split(
    'filename='
  )[1];
  const effectiveFileName = suggestedFileName ?? 'report.xlsx';
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // IE variant
    window.navigator.msSaveOrOpenBlob(
      new Blob([response.data], {
        type: contentType,
      }),
      effectiveFileName
    );
  } else {
    const url = window.URL.createObjectURL(
      new Blob([response.data], {
        type: contentType,
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', effectiveFileName);
    document.body.appendChild(link);
    link.click();
  }
};

const useApproachingReportExport = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState();
  const getData = async () => {
    try {
      setLoading(true);
      await reportApi.getApproachingReportExcel(filters).then(saveReport);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && filters) {
      getData().then();
    }
  }, [filters]);

  return {
    loading,
    setFilters,
  };
};

const useTeam = (getAll?: boolean) => {
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState<ITeam[]>([]);

  const getTeams = async () => {
    try {
      setLoading(true);
      const result = await reportApi.getTeam({
        ...reportConstants.DEFAULT_PARAMS_ALL,
        getAll,
      });
      setTeams(orderBy(result.data?.teams, ['teamName'], ['asc']));
    } catch {
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTeams().then();
  }, []);

  return {
    loading,
    teams,
    getTeams,
  };
};

const useSale = () => {
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState<ISaleStaff[]>([]);
  const [teamId, setTeamId] = useState<number | undefined>(undefined);

  const getSales = async () => {
    try {
      setLoading(true);
      const result = await reportApi.getSale({
        ...reportConstants.DEFAULT_PARAMS_ALL,
        teamIds: teamId,
      });
      setSales(result.data?.staffs);
    } catch {
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSales().then();
  }, [teamId]);

  return {
    loading,
    setTeamId,
    sales,
    getSales,
  };
};

const useNewShops = () => {
  const [newShops, setNewShops] = useState<INewShop[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [params, setParams] = useState<IGetNewShopsParams>(DEFAULT_PARAMS);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const getNewShops = async () => {
    try {
      setLoading(true);
      const res = await shopServices.getNewShops(params);
      if (res) {
        const { pagination, shopNews } = res;

        setNewShops(shopNews);
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const renderQuery = (values: AnyObject) => {
    if (
      values.transactionFilter &&
      values.transactionFilter.includes(commonConstants.ALL)
    ) {
      values.transactionFilter = undefined;
    }
    if (
      values.additionalShopType &&
      values.additionalShopType === commonConstants.ALL
    ) {
      values.additionalShopType = undefined;
    }
    if (values.createDate && values.createDate.length) {
      values.createdFromDate = dayjs(values.createDate[0])
        .startOf('day')
        .format(DATE_SLASH_FORMAT);
      values.createdToDate = dayjs(values.createDate[1])
        .endOf('day')
        .format(DATE_SLASH_FORMAT);
    }
    values.locationCode =
      values.wardCode || values.districtCode || values.provinceCode;

    if (values.shopId) {
      values.shopId = [values.shopId];
    }
    if (values.staffId) {
      values.staffId = [values.staffId];
    }
    return {
      ...pickBy(values, (e, key) => {
        return (
          e !== '' &&
          e !== undefined &&
          e !== null &&
          !(typeof e === 'object' && e.length === 0) &&
          !['provinceCode', 'districtCode', 'wardCode', 'createDate'].includes(
            key
          )
        );
      }),
    };
  };

  const onSearch = (values: AnyObject) => {
    const query = renderQuery(values);
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const onClearFilter = () => {
    setParams(DEFAULT_PARAMS);
  };

  const onExportExcel = async (values: AnyObject) => {
    try {
      setLoadingExport(true);
      const query = renderQuery(values);
      const fileName = 'Danh sách shop.xlsx';
      const res = await shopServices.exportNewShops(query);
      if (res.filePath) {
        await fileService.downloadFileToDevice(res.filePath, fileName);
      }
    } finally {
      setLoadingExport(false);
    }
  };
  useEffect(() => {
    if (!!countFilter(omit(params, ['page', 'pageSize']))) getNewShops();
  }, [params]);

  return {
    getNewShops,
    newShops,
    loading,
    handleTableChange,
    paginationAnt,
    onSearch,
    onClearFilter,
    setLoading,
    params,
    onExportExcel,
    loadingExport,
  };
};

const useDailyReports = () => {
  const [params, setParams] = useState<IGetDailyReportParams>(
    DEFAULT_PARAMS_DAILY_REPORT
  );
  const [jobProcessStatisticTeam, setJobProcessStatisticTeam] = useState<
    IJobProcessStatisticTree[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });
  const [type, setType] = useState<number>(
    REPORTABLE_ACCORDING_TO.BY_DATE.value
  );

  const getDailyReports = async () => {
    try {
      setLoading(true);
      const res = await reportServices.getDailyReport(params);
      if (res) {
        const { pagination, teams } = res;
        setJobProcessStatisticTeam(convertJobToTree(teams));
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const convertJobToTree = (jobProcess: IJobProcessStatisticTeam[]) => {
    return jobProcess.map(jobPro => {
      return {
        ...omit(jobPro, ['staffs']),
        children: jobPro.staffs.map(jobStaff => {
          return {
            ...jobStaff,
            teamName: `${jobStaff.staffFullName}- ${jobStaff.staffEmail}`,
          };
        }),
      };
    });
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const renderQuery = (values: AnyObject) => {
    return {
      ...pickBy(values, (e, key) => {
        return e !== '' && e !== undefined && e !== null && key !== 'type';
      }),
    };
  };

  const onSearch = (values: AnyObject) => {
    let query = renderQuery(values);
    setType(values.type);
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const onClearFilter = () => {
    setParams(DEFAULT_PARAMS_DAILY_REPORT);
  };

  const onExportExcel = async (values: AnyObject) => {
    try {
      setLoadingExport(true);
      const fileName = 'Báo cáo tiến độ.xlsx';
      let query = renderQuery(values);
      const res = await reportServices.exportDailyShops(
        omit(query, ['page', 'pageSize', 'orderBy'])
      );
      if (res.filePath)
        await fileService.downloadFileToDevice(res.filePath, fileName);
    } finally {
      setLoadingExport(false);
    }
  };
  useEffect(() => {
    getDailyReports();
  }, [params]);

  return {
    getDailyReports,
    jobProcessStatisticTeam,
    loading,
    handleTableChange,
    paginationAnt,
    onSearch,
    onClearFilter,
    setLoading,
    params,
    onExportExcel,
    loadingExport,
    type,
  };
};

const useProgressReport = () => {
  const [params, setParams] = useState<IGetMerchantApproachingParams>(
    DEFAULT_PARAMS
  );
  const [listMerchantApproach, setListMerchantApproach] = useState<
    IMerchantApproachingProcess[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const getMerchantApproachs = async () => {
    try {
      setLoading(true);
      const res = await merchantService.getMerchantApproaching(params);
      if (res) {
        const { pagination, approachingMerchants } = res.data;
        setListMerchantApproach(approachingMerchants);
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const renderQuery = (values: AnyObject) => {
    return {
      ...pickBy(values, e => {
        return e !== '' && e !== undefined && e !== null;
      }),
    };
  };

  const onSearch = (values: AnyObject) => {
    let query = renderQuery(values);
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const onUpdateMerchant = async (
    merchantId: number,
    value: IUpdateMerchantApproaching
  ) => {
    await merchantService.updateMerchantApproaching(merchantId, value);
    await getMerchantApproachs();
  };

  const onExportExcel = async (values: AnyObject) => {
    try {
      setLoadingExport(true);
      const fileName = 'Báo cáo tiến độ.xlsx';
      let query = renderQuery(values);
      const res = await merchantService.exportProgressReport(
        omit(query, ['page', 'pageSize'])
      );
      if (res.filePath)
        await fileService.downloadFileToDevice(res.filePath, fileName);
    } finally {
      setLoadingExport(false);
    }
  };
  useEffect(() => {
    getMerchantApproachs();
  }, [params]);

  return {
    getMerchantApproachs,
    listMerchantApproach,
    loading,
    handleTableChange,
    paginationAnt,
    onSearch,
    onUpdateMerchant,
    setLoading,
    params,
    onExportExcel,
    loadingExport,
  };
};

const useMerchantApproachHistory = () => {
  const [approachHistory, setApproachHistory] = useState<
    IMerchantApproachHistory[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const getMerchantApproachHistory = async (id: number) => {
    try {
      setLoading(true);
      const res = await merchantService.getMerchantApproachHistory(id);
      setApproachHistory(res.merchantHistories);
    } finally {
      setLoading(false);
    }
  };

  return {
    getMerchantApproachHistory,
    loading,
    approachHistory,
  };
};

const useListShopTakeCare = () => {
  const [listShopTakeCare, setListShopTakeCare] = useState<IShopTakeCare[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [params, setParams] = useState<IGetShopTakeCareParams>(DEFAULT_PARAMS);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const getListShopTakeCare = async () => {
    try {
      setLoading(true);
      let paramsSearch = {
        ...pickBy(params, e => {
          return e !== ALL_VALUE_NUMBER;
        }),
      };

      const res = await shopServices.getListShopTakeCare(paramsSearch);
      if (res) {
        const { pagination, shopTakeCares } = res;
        setListShopTakeCare(shopTakeCares);
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const renderQuery = (values: AnyObject) => {
    if (values.takeCareDate && values.takeCareDate.length) {
      values.takeCareFromDate = dayjs(values.takeCareDate[0]).format(
        YEAR_DASH_FORMAT
      );
      values.takeCareToDate = dayjs(values.takeCareDate[1]).format(
        YEAR_DASH_FORMAT
      );
    }
    values.locationCode =
      values.wardCode || values.districtCode || values.provinceCode;

    return {
      ...pickBy(values, (e, key) => {
        return (
          e !== '' &&
          e !== undefined &&
          e !== null &&
          !(typeof e === 'object' && e.length === 0) &&
          ![
            'provinceCode',
            'districtCode',
            'wardCode',
            'takeCareDate',
          ].includes(key)
        );
      }),
    };
  };

  const onSearch = (values: AnyObject) => {
    const query = renderQuery(values);
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const onClearFilter = () => {
    setParams(DEFAULT_PARAMS);
  };

  const onExportExcel = async (values: AnyObject) => {
    try {
      setLoadingExport(true);
      const query = renderQuery(values);
      const fileName = 'Danh sách shop chăm sóc.xlsx';
      let querySearch = {
        ...pickBy(query, e => {
          return e !== ALL_VALUE_NUMBER;
        }),
      };
      const res = await shopServices.exportListShopTakeCare(querySearch);
      if (res?.filePath) {
        await fileService.downloadFileToDevice(res.filePath, fileName);
      }
    } finally {
      setLoadingExport(false);
    }
  };
  useEffect(() => {
    if (!!countFilter(omit(params, ['page', 'pageSize'])))
      getListShopTakeCare();
  }, [params]);

  return {
    getListShopTakeCare,
    listShopTakeCare,
    loading,
    handleTableChange,
    paginationAnt,
    onSearch,
    onClearFilter,
    setLoading,
    params,
    onExportExcel,
    loadingExport,
  };
};

const useListShopTakeCareOfStaff = () => {
  const [params, setParams] = useState<IGetShopTakeCareStaffParams>(
    DEFAULT_PARAMS
  );
  const [listShopTakeCare, setListShopTakeCare] = useState<
    IShopTakeCareStaff[]
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalShop, setTotalShop] = useState<number>(0);

  const getListShopTakeCareStaff = async () => {
    try {
      if (params.page === defaultPage) setLoading(true);

      const res = await shopServices.getListShopTakeCareStaff(params);
      const { shopTakeCares, pagination } = res;
      setTotalShop(pagination.totalItems);
      setHasMore(
        pagination.currentPage * pagination.pageSize < pagination.totalItems
      );
      if (params.page === defaultPage) setListShopTakeCare(shopTakeCares);
      else {
        setListShopTakeCare([...listShopTakeCare, ...shopTakeCares]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getListShopTakeCareStaff();
  }, [params]);

  return {
    listShopTakeCare,
    setParams,
    params,
    hasMore,
    loading,
    getListShopTakeCareStaff,
    totalShop,
  };
};

export default {
  useApproachingReport,
  useApproachingReportExport,
  useSale,
  useTeam,
  useNewShops,
  useDailyReports,
  useListShopTakeCare,
  useListShopTakeCareOfStaff,
  useProgressReport,
  useMerchantApproachHistory,
};
